/**
 * Light, dark, or whatever this machine is set to.
 *
 * Three options rather than a toggle, because a toggle cannot express *system* — and
 * *system* is the default, so a toggle would leave the state everybody starts in
 * unreachable once they had left it.
 *
 * Beside the language select in the person's menu, and kept the same way: applied by the browser at once, and
 * remembered on the person's membership so it follows them to the next desk. The browser's
 * own copy is what paints the sign-in page before anybody has signed in.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { choose, onSchemeChange, preference, type Preference } from '../theme';
import { Select } from '../ui';
import { usePreferences } from './preferencesContext';

const OPTIONS: readonly Preference[] = ['system', 'light', 'dark'];

export function ThemeSelect({ className = 'w-auto' }: { className?: string }) {
  const { t } = useTranslation();

  // The module is the authority and does not re-render anything, so the control holds its
  // own copy of the preference.
  const [chosen, setChosen] = useState<Preference>(preference);
  const { save } = usePreferences();

  // And subscribes, so that the copy cannot go stale. A second tab on the same workstation
  // writes the key, `theme.ts` hears the storage event and repaints — and without this the
  // select would still be showing the choice this tab made an hour ago.
  useEffect(() => onSchemeChange(() => setChosen(preference())), []);

  return (
    <Select
      value={chosen}
      onChange={(event) => {
        const next = event.target.value as Preference;

        setChosen(next);
        choose(next);
        save({ theme: next });
      }}
      aria-label={t('theme.label')}
      className={className}
    >
      {OPTIONS.map((option) => (
        <option key={option} value={option}>
          {t(`theme.names.${option}`)}
        </option>
      ))}
    </Select>
  );
}
